import { Device, Document, HARDCODED_TEMPLATE_KEY } from "@models";
import {
  MoreVert,
  NoteAddOutlined,
  UploadFileOutlined,
} from "@mui/icons-material";
import CreateNewFolderOutlinedIcon from "@mui/icons-material/CreateNewFolderOutlined";
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  Menu,
  MenuItem,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { RegulatoryTypeEnum, Template, TemplateConfig } from "@types";
import {
  MouseEvent as ReactMouseEvent,
  ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useCreateFolderMutation } from "src/hooks";
import { useGetDocumentsTemplates } from "src/hooks/queries";
import { useCustomTemplate } from "src/hooks/useCustomTemplate";
import { useUserUploadTemplate } from "src/hooks/useUserUpload";
import {
  buildDocumentRoute,
  captureSentryException,
  DEFAULT_ACCEPTED_FILE_TYPES,
} from "src/utils";

type DocumentActionsMenuTriggerProps = {
  ariaControls?: string;
  ariaExpanded?: boolean;
  ariaHasPopup: true;
  id: string;
  onClick: (event: ReactMouseEvent<HTMLButtonElement>) => void;
  onMouseDown?: (event: ReactMouseEvent<HTMLButtonElement>) => void;
};

type DocumentActionsDeviceOption = Pick<Device, "id" | "name">;
const EMPTY_DEVICE_OPTIONS: DocumentActionsDeviceOption[] = [];
const EMPTY_DOCUMENTS: Document[] = [];

type CreateDocumentTemplateOption = {
  label: string;
  template: Template;
  templateKey: string;
};

type DocumentActionsMenuProps = {
  deviceId: string | null;
  devices?: DocumentActionsDeviceOption[];
  documents?: Document[];
  extraMenuItems?: ReactNode;
  getTemplatesConfigForDevice?: (
    deviceId: string
  ) => TemplateConfig | undefined;
  onUploadSuccess?: (uploadedDocuments: Document[]) => void;
  renderTrigger?: (props: DocumentActionsMenuTriggerProps) => ReactNode;
  targetFolderId?: string | null;
  templatesConfig?: TemplateConfig;
  triggerDataTestId?: string;
};

export const DocumentActionsMenu = ({
  deviceId,
  devices = EMPTY_DEVICE_OPTIONS,
  documents = EMPTY_DOCUMENTS,
  extraMenuItems,
  getTemplatesConfigForDevice,
  onUploadSuccess,
  renderTrigger,
  targetFolderId,
  templatesConfig,
  triggerDataTestId,
}: DocumentActionsMenuProps) => {
  const { orgId = "" } = useParams();
  const navigate = useNavigate();
  const createFolderMutation = useCreateFolderMutation();
  const fallbackDevices = deviceId
    ? [
        {
          id: deviceId,
          name: "Selected device",
        },
      ]
    : EMPTY_DEVICE_OPTIONS;
  const availableDevices = devices.length > 0 ? devices : fallbackDevices;
  const initialSelectedDeviceId = deviceId ?? availableDevices[0]?.id ?? null;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isCreateFolderDialogOpen, setIsCreateFolderDialogOpen] =
    useState(false);
  const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
  const [documentName, setDocumentName] = useState("");
  const [folderName, setFolderName] = useState("");
  const [isCreateFromTemplate, setIsCreateFromTemplate] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState<string | null>(
    null
  );
  const [selectedDeviceId, setSelectedDeviceId] = useState<string | null>(
    initialSelectedDeviceId
  );
  const fileInput = useRef<HTMLInputElement>(null);
  const createDocumentNameInputRef = useRef<HTMLInputElement>(null);
  const createFolderNameInputRef = useRef<HTMLInputElement>(null);
  const menuId = useId();
  const triggerId = `${menuId}-trigger`;
  const isMenuOpen = Boolean(anchorEl);
  const isCreatingFolder = createFolderMutation.isPending;
  const selectedDevice =
    availableDevices.find((device) => device.id === selectedDeviceId) ?? null;
  const { data: orgTemplatesConfig } = useGetDocumentsTemplates({
    orgId,
    deviceId: "",
    enabled: isCreateDialogOpen && !selectedDeviceId && isCreateFromTemplate,
  });
  const selectedTemplatesConfig = selectedDeviceId
    ? (getTemplatesConfigForDevice?.(selectedDeviceId) ??
      (selectedDeviceId === deviceId ? templatesConfig : undefined))
    : orgTemplatesConfig;
  const templateOptions = useMemo<CreateDocumentTemplateOption[]>(
    () =>
      Object.values(selectedTemplatesConfig ?? {})
        .filter((template) => {
          if (
            (!selectedDeviceId && !template.isOrgDocument) ||
            template.hidden ||
            template.userUpload ||
            template.deprecatedByTemplateKey
          ) {
            return false;
          }

          if (template.regulatoryType === RegulatoryTypeEnum.RCD) {
            return true;
          }

          return !documents.some(
            (document) =>
              document.templateKey === template.templateKey &&
              document.deviceId ===
                (template.isOrgDocument ? null : selectedDeviceId)
          );
        })
        .map<CreateDocumentTemplateOption>((template) => ({
          label: template.defaultTitle,
          template,
          templateKey: template.templateKey,
        }))
        .sort((first, second) => first.label.localeCompare(second.label)),
    [documents, selectedDeviceId, selectedTemplatesConfig]
  );
  const selectedTemplateOption =
    templateOptions.find(
      (option) => option.templateKey === selectedTemplateKey
    ) ?? null;
  const selectedTemplate = selectedTemplateOption?.template ?? null;
  const isSelectedRcdTemplate =
    selectedTemplate?.regulatoryType === RegulatoryTypeEnum.RCD;
  const hasDuplicateCreateName = documents.some(
    (document) =>
      document.deviceId ===
        (selectedTemplate?.isOrgDocument ? null : selectedDeviceId) &&
      typeof document.title === "string" &&
      document.title === documentName
  );
  const createValidationError =
    isCreateDialogOpen &&
    hasDuplicateCreateName &&
    !(isCreateFromTemplate && isSelectedRcdTemplate) &&
    !isCreating
      ? "A document with this name already exists."
      : "";
  const { createCustomDocument, createDocumentFromTemplate } =
    useCustomTemplate(orgId, selectedDeviceId, {
      folderId: targetFolderId,
      ...(!selectedDeviceId
        ? { navigationDeviceId: initialSelectedDeviceId }
        : {}),
    });
  const { uploadFileAsDocument } = useUserUploadTemplate(
    orgId,
    selectedDeviceId ?? "",
    {
      folderId: targetFolderId,
    }
  );

  useEffect(() => {
    if (!isCreateDialogOpen) {
      return;
    }

    const animationFrameId = requestAnimationFrame(() => {
      createDocumentNameInputRef.current?.focus();
    });

    return () => cancelAnimationFrame(animationFrameId);
  }, [isCreateDialogOpen]);

  useEffect(() => {
    if (!isCreateDialogOpen || !isCreateFromTemplate || !selectedTemplateKey) {
      return;
    }

    if (
      !templateOptions.some(
        (option) => option.templateKey === selectedTemplateKey
      )
    ) {
      setSelectedTemplateKey(null);
      setDocumentName("");
    }
  }, [
    isCreateDialogOpen,
    isCreateFromTemplate,
    selectedTemplateKey,
    templateOptions,
  ]);

  useEffect(() => {
    if (!isCreateFolderDialogOpen) {
      return;
    }

    const animationFrameId = requestAnimationFrame(() => {
      createFolderNameInputRef.current?.focus();
    });

    return () => cancelAnimationFrame(animationFrameId);
  }, [isCreateFolderDialogOpen]);

  const resetCreateState = () => {
    setDocumentName("");
    setIsCreateFromTemplate(false);
    setSelectedTemplateKey(null);
  };

  const resetCreateFolderState = () => {
    setFolderName("");
  };

  const resetSelectedDevice = () => {
    setSelectedDeviceId(initialSelectedDeviceId);
  };

  const handleOpenMenu = (event: ReactMouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleTriggerMouseDown = (
    event: ReactMouseEvent<HTMLButtonElement>
  ) => {
    event.stopPropagation();
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleOpenCreateDialog = () => {
    resetCreateState();
    resetSelectedDevice();
    setIsCreateDialogOpen(true);
    handleCloseMenu();
  };

  const handleCloseCreateDialog = () => {
    if (isCreating) {
      return;
    }

    setIsCreateDialogOpen(false);
  };

  const handleOpenCreateFolderDialog = () => {
    resetCreateFolderState();
    setIsCreateFolderDialogOpen(true);
    handleCloseMenu();
  };

  const handleCloseCreateFolderDialog = () => {
    if (isCreatingFolder) {
      return;
    }

    setIsCreateFolderDialogOpen(false);
  };

  const handleOpenUploadDialog = () => {
    if (isUploading) {
      return;
    }

    resetSelectedDevice();
    setIsUploadDialogOpen(true);
    handleCloseMenu();
  };

  const handleCloseUploadDialog = () => {
    if (isUploading) {
      return;
    }

    setIsUploadDialogOpen(false);
  };

  const handleDocumentNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setDocumentName(event.target.value);
  };

  const handleCreateFromTemplateChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setIsCreateFromTemplate(event.target.checked);
    setSelectedTemplateKey(null);
    setDocumentName("");
  };

  const handleTemplateChange = (
    _event: React.SyntheticEvent,
    nextOption: CreateDocumentTemplateOption | null
  ) => {
    if (!nextOption) {
      setSelectedTemplateKey(null);
      setDocumentName("");
      return;
    }

    setSelectedTemplateKey(nextOption.templateKey);
    setDocumentName(nextOption.template.defaultTitle);
  };

  const handleFolderNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFolderName(event.target.value);
  };

  const handleCreateNewDocument = async () => {
    if (isCreateFromTemplate && !selectedTemplateOption) {
      return;
    }

    setIsCreating(true);

    try {
      const nextDocumentName = documentName.trim();

      if (isCreateFromTemplate && selectedTemplateOption) {
        await createDocumentFromTemplate(
          selectedTemplateOption.templateKey,
          nextDocumentName
        );
      } else {
        await createCustomDocument(nextDocumentName);
      }

      setIsCreateDialogOpen(false);
    } catch (error) {
      captureSentryException(error, {
        documents,
        error,
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateFolder = async () => {
    const nextFolderName = folderName.trim();

    if (!nextFolderName || isCreatingFolder) {
      return;
    }

    await createFolderMutation.mutateAsync({
      orgId,
      data: {
        name: nextFolderName,
        parentFolderId: targetFolderId ?? null,
      },
    });

    setIsCreateFolderDialogOpen(false);
    setFolderName("");
  };

  const handleConfirmUpload = () => {
    if (!selectedDeviceId || isUploading) {
      return;
    }

    setIsUploadDialogOpen(false);
    fileInput.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!selectedDeviceId) {
      return;
    }

    const files = Array.from(event.target.files || []);

    if (files.length === 0) {
      return;
    }

    setIsUploading(true);

    try {
      const uploadedDocuments = await Promise.all(
        files.map(async (file) => uploadFileAsDocument(file))
      );

      if (uploadedDocuments.length > 0) {
        navigate(
          buildDocumentRoute({
            documentId: uploadedDocuments[0].id,
            deviceId: selectedDeviceId,
            orgId,
            templateKey:
              uploadedDocuments[0].templateKey ??
              HARDCODED_TEMPLATE_KEY.USER_UPLOAD,
          })
        );
      }

      onUploadSuccess?.(uploadedDocuments);
    } catch (error) {
      captureSentryException(error, {
        documents,
        error,
      });
    } finally {
      setIsUploading(false);

      if (fileInput.current) {
        fileInput.current.value = "";
      }
    }
  };

  const trigger = renderTrigger ? (
    renderTrigger({
      ariaControls: isMenuOpen ? menuId : undefined,
      ariaExpanded: isMenuOpen ? true : undefined,
      ariaHasPopup: true,
      id: triggerId,
      onClick: handleOpenMenu,
      onMouseDown: handleTriggerMouseDown,
    })
  ) : (
    <IconButton
      aria-controls={isMenuOpen ? menuId : undefined}
      aria-expanded={isMenuOpen ? true : undefined}
      aria-haspopup="menu"
      aria-label="Document actions"
      data-testid={triggerDataTestId}
      id={triggerId}
      onClick={handleOpenMenu}
      onMouseDown={handleTriggerMouseDown}
      size="small"
      sx={{ color: "rgba(0, 0, 0, 0.54)", padding: 0.5 }}
    >
      <MoreVert />
    </IconButton>
  );

  return (
    <>
      {trigger}

      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={(_event, _reason) => handleCloseMenu()}
        sx={{
          pointerEvents: isMenuOpen ? "auto" : "none",
        }}
        MenuListProps={{
          "aria-labelledby": triggerId,
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        BackdropProps={{
          onClick: (event: ReactMouseEvent) => {
            event.stopPropagation();
            event.nativeEvent.stopImmediatePropagation();
            handleCloseMenu();
          },
          onMouseDown: (event: ReactMouseEvent) => {
            event.stopPropagation();
            event.nativeEvent.stopImmediatePropagation();
          },
        }}
      >
        <MenuItem
          sx={{ display: "flex", alignItems: "center", gap: "12px" }}
          onClick={handleOpenCreateFolderDialog}
        >
          <CreateNewFolderOutlinedIcon fontSize="small" />
          New Folder
        </MenuItem>
        <MenuItem
          sx={{ display: "flex", alignItems: "center", gap: "12px" }}
          onClick={handleOpenCreateDialog}
        >
          <NoteAddOutlined fontSize="small" />
          New Document
        </MenuItem>
        <MenuItem
          onClick={handleOpenUploadDialog}
          disabled={isUploading || availableDevices.length === 0}
          sx={{ display: "flex", alignItems: "center", gap: "12px" }}
        >
          <UploadFileOutlined fontSize="small" />
          Upload Document
        </MenuItem>
        {extraMenuItems}
      </Menu>

      <Dialog
        open={isCreateDialogOpen}
        onClose={handleCloseCreateDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Document</DialogTitle>
        <DialogContent>
          <div className="flex flex-col gap-4 pt-2">
            <FormControl>
              <FormLabel id={`${menuId}-scope-label`}>Scope</FormLabel>
              <RadioGroup
                aria-labelledby={`${menuId}-scope-label`}
                value={selectedDeviceId ? "device" : "organization"}
                onChange={(_, scope) => {
                  setSelectedDeviceId(
                    scope === "device" ? initialSelectedDeviceId : null
                  );
                }}
                className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2"
              >
                {[
                  {
                    value: "organization",
                    label: "Organization-wide",
                    description: "Not linked to a device",
                  },
                  {
                    value: "device",
                    label: "Specific device",
                    description: "Linked to one device",
                  },
                ].map(({ value, label, description }) => (
                  <FormControlLabel
                    key={value}
                    value={value}
                    disabled={
                      value === "device" && availableDevices.length === 0
                    }
                    control={<Radio />}
                    className="m-0 rounded-lg border border-solid p-2"
                    sx={{
                      borderColor:
                        (value === "device") === Boolean(selectedDeviceId)
                          ? "primary.main"
                          : "divider",
                      bgcolor:
                        (value === "device") === Boolean(selectedDeviceId)
                          ? "action.selected"
                          : "background.paper",
                    }}
                    label={
                      <div>
                        <Typography variant="body2" fontWeight={600}>
                          {label}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {description}
                        </Typography>
                      </div>
                    }
                  />
                ))}
              </RadioGroup>
            </FormControl>
            {selectedDeviceId ? (
              <Autocomplete
                disableClearable
                options={availableDevices}
                value={selectedDevice ?? undefined}
                onChange={(_, nextDevice) => {
                  setSelectedDeviceId(nextDevice.id);
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField {...params} label="Device" />
                )}
              />
            ) : null}
            <FormControlLabel
              control={
                <Switch
                  checked={isCreateFromTemplate}
                  onChange={handleCreateFromTemplateChange}
                />
              }
              label="Create from frame"
            />
            {isCreateFromTemplate ? (
              <Autocomplete
                disabled={templateOptions.length === 0}
                forcePopupIcon={false}
                noOptionsText="No templates available"
                options={templateOptions}
                value={selectedTemplateOption}
                onChange={handleTemplateChange}
                isOptionEqualToValue={(option, value) =>
                  option.templateKey === value.templateKey
                }
                getOptionLabel={(option) => option.label}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    helperText={
                      selectedDeviceId
                        ? "Non-RCD frames already used for the device are hidden."
                        : "Non-RCD frames already used for the organization are hidden."
                    }
                    label="Frame"
                  />
                )}
              />
            ) : null}
            <TextField
              label="Document Name"
              value={documentName}
              className="w-full"
              inputRef={createDocumentNameInputRef}
              onChange={handleDocumentNameChange}
              error={!!createValidationError}
              helperText={createValidationError}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleCloseCreateDialog}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateNewDocument}
            disabled={
              !documentName.trim() ||
              !!createValidationError ||
              isCreating ||
              (isCreateFromTemplate && !selectedTemplateOption)
            }
          >
            {isCreating ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Create"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isCreateFolderDialogOpen}
        onClose={handleCloseCreateFolderDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Create folder</DialogTitle>
        <DialogContent>
          <TextField
            autoComplete="off"
            inputRef={createFolderNameInputRef}
            label="Folder name"
            onChange={handleFolderNameChange}
            sx={{ mt: 1, width: "100%" }}
            value={folderName}
          />
        </DialogContent>
        <DialogActions>
          <Button
            color="inherit"
            disabled={isCreatingFolder}
            onClick={handleCloseCreateFolderDialog}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            disabled={!folderName.trim() || isCreatingFolder}
            onClick={handleCreateFolder}
            variant="contained"
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isUploadDialogOpen}
        onClose={handleCloseUploadDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Upload Documents</DialogTitle>
        <DialogContent>
          <div className="flex flex-col gap-4 pt-2">
            <Typography color="text.secondary" variant="body2">
              Choose which device this uploaded document belongs to before
              continuing.
            </Typography>
            <Autocomplete
              disableClearable
              options={availableDevices}
              value={selectedDevice ?? undefined}
              onChange={(_, nextDevice) => {
                setSelectedDeviceId(nextDevice?.id ?? null);
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => option.name}
              renderInput={(params) => <TextField {...params} label="Device" />}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleCloseUploadDialog}
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmUpload}
            disabled={!selectedDeviceId || isUploading}
          >
            Upload Documents
          </Button>
        </DialogActions>
      </Dialog>

      <input
        id="hidden-qms-file-input"
        type="file"
        className="hidden"
        accept={DEFAULT_ACCEPTED_FILE_TYPES}
        multiple
        data-testid="hidden-qms-file-input"
        ref={fileInput}
        onChange={handleFileChange}
      />
    </>
  );
};
